import { Renderer, VisualOptions } from './renderer';
import { UI } from './ui';
import { debounce } from './helpers/helpers';
import { isAreasDataValid } from './validators/validators';
import { EditMode } from './model/edit-mode';
import { Requester } from './requester';
import { Translator } from './translator';
import { EditState } from './edit-state/edit-state';

const additionalOptionsDefaults = {
  translations: {},
  selectedLayout: null,
  availableBoards: null,
  freeDirection: true,
  boardLengthsSelection: true,
  bordersSelectable: true,
  splitting: true,
  initialPlanCalculation: false,
};

export default class Configurator {
  editMode = EditMode.POINTER;
  activePlanarea = null;
  activeBorder = null;
  additionalOptions = {};

  constructor(options = {}) {
    ({
      canvasSelector: this.canvasSelector,
      toolbarSelector: this.toolbarSelector,
      formSelector: this.formSelector,
      configuratorSelector: this.configuratorSelector,
      sidebarSelector: this.sidebarSelector,
      errorSelector: this.errorSelector,
      statsSelector: this.statsSelector,
      priceStatsSelector: this.priceStatsSelector,
      formatPatternScale: this.formatPatternScale,
    } = options);

    this.displayedParallelLayoutOptions = options.parallelStaggeredOptions;
    Object.assign(this.additionalOptions, additionalOptionsDefaults, options.additionalOptions);

    if (!isAreasDataValid(options.areas)) {
      console.error('configurator inputs are not valid');
      document.querySelector(this.configuratorSelector).style.display = 'none';
      document.querySelector(this.errorSelector).style.display = 'block';
      return;
    }
    this.editState = this.createEditState(options.areas);

    this.translator = new Translator({ translations: options.translations });

    this.renderer = new Renderer({
      canvasSelector: this.canvasSelector,
      editState: this.editState,
      editMode: this.editMode,
      onBorderActivated: this.borderActivated.bind(this),
      onPlanareaActivated: this.planareaActivated.bind(this),
      onBaseBorderChanged: this.baseBorderChanged.bind(this),
      onPlanareaSplitted: this.planareaSplitted.bind(this),
      additionalOptions: this.additionalOptions,
      translator: this.translator,
      formatPatternScale: this.formatPatternScale,
    });

    this.ui = new UI({
      toolbarSelector: this.toolbarSelector,
      canvasSelector: this.canvasSelector,
      sidebarSelector: this.sidebarSelector,
      configuratorSelector: this.configuratorSelector,
      statsSelector: this.statsSelector,
      priceStatsSelector: this.priceStatsSelector,
      editMode: this.editMode,
      onZoomIn: this.zoomIn.bind(this),
      onZoomOut: this.zoomOut.bind(this),
      onModeChanged: this.changeMode.bind(this),
      onBorderMenuClosed: this.borderMenuClosed.bind(this),
      onAreaMenuClosed: this.areaMenuClosed.bind(this),
      onBaseBorderChangeRequest: this.baseBorderChangeRequested.bind(this),
      onBoardsLayoutChanged: this.boardsLayoutChanged.bind(this),
      onBoardsLayoutChangedStep: this.boardsLayoutChangedStep.bind(this),
      onSelectedBoardLengthsChanged: this.selectedBoardLengthsChanged.bind(this),
      onInstallationTypeChanged: this.installationTypeChanged.bind(this),
      onSplitCanceled: this.splitCanceled.bind(this),
      onSplitPerpendicular: this.splitPerpendicularRequested.bind(this),
      onSplitParallel: this.splitParallelRequested.bind(this),
      onBreakerBoardsPlaced: this.breakerBoardsPlaced.bind(this),
      onAngleChanged: this.angleChanged.bind(this),
      onBorderDelete: this.borderDeleted.bind(this),
      onTileShiftChanged: this.tileShiftChanged.bind(this),
      onJoistSpacingPerpendicularChanged: this.joistSpacingPerpendicularChanged.bind(this),
      onJoistSpacingParallelChanged: this.joistSpacingParallelChanged.bind(this),
      onTrimLabelChanged: this.trimLabelChanged.bind(this),
      translator: this.translator,
      additionalOptions: this.additionalOptions,
    });

    this.ui.updateStats(this.editState);

    this.requester = new Requester();

    this.handleOnPageResize();
  }

  createEditState(rawareas) {
    let descriptorsCount = 0;
    rawareas.forEach(area => {
      if (area.data.descriptor) {
        descriptorsCount++;
      }
    });
    if (descriptorsCount && rawareas.length !== descriptorsCount) {
      throw Error('Not every area has its own descriptor, that should not happen');
    } else {
      return new EditState({
        rawAreas: rawareas,
        selectedLayouts: this.additionalOptions.selectedLayout,
        availableBoards: this.additionalOptions.availableBoards,
        displayedParallelLayoutOptions: this.displayedParallelLayoutOptions,
        edgeLabelingOptions: this.additionalOptions.edgeLabelingOptions,
        edgeLabelingMapping: this.additionalOptions.edgeLabelingMapping,
      });
    }
  }

  handleOnPageResize() {
    window.addEventListener(
      'resize',
      debounce(() => {
        this.renderer.fitPlanToView();
        this.renderer.redraw();
      }, 100)
    );
  }

  _addPlanareaData(form) {
    const objects = this.editState.backendObjectsForSubmit;
    objects.forEach((object, index) => {
      const input = document.createElement('input');
      input.setAttribute('type', 'hidden');
      input.setAttribute('name', 'verlegeplan-' + object.descriptor.id);
      input.setAttribute('value', object.current_plan_index);
      form.appendChild(input);
    });
  }

  _performSubmit() {
    const form = document.querySelector(this.formSelector);
    if (form) {
      this._addPlanareaData(form);
      form.submit();
    }
  }

  sendFormData() {
    this.requester.recalculate(
      this.editState.backendObjects,
      () => {
        this._performSubmit();
      },
      data => {
        this._recalculationFailedHandling(data);
      },
      false,
      false
    );
  }

  zoomIn() {
    this.renderer.zoomIn();
  }

  zoomOut() {
    this.renderer.zoomOut();
  }

  changeMode(mode) {
    this.editMode = mode;
    this.renderer.switchEditMode(this.editMode);
  }

  borderActivated(border) {
    this.activeBorder = border;
    this.ui.borderActivated(this.activeBorder);
    this.renderer.redraw();
  }

  planareaActivated(planarea) {
    this._deactivateBorder();
    this.activePlanarea = planarea;
    this.ui.planareaActivated(this.activePlanarea, this.editState.areas.length === 1);
    this.renderer.fitPlanToView();
    this.renderer.redraw();
  }

  borderMenuClosed() {
    this.activePlanarea.deactivateBorder();
    this.renderer.redraw();
  }

  areaMenuClosed() {
    this.activePlanarea.deactivate();
    this.activePlanarea = null;
    this.ui.planareasDeactivated();
    this.renderer.fitPlanToView();
    this.renderer.redraw();
  }

  _deactivateBorder() {
    if (this.activeBorder) {
      if (this.activePlanarea) {
        this.activePlanarea.deactivateBorder();
      }
      this.activeBorder = null;
    }
  }

  baseBorderChangeRequested() {
    this.activePlanarea.editBaseBorder();
    this.renderer.redraw();
  }

  baseBorderChanged(border) {
    if (!border.isBaseBorder) {
      this.activePlanarea.areaState.updateRawAreaData('active_edge', border.index);
      this.activePlanarea.areaState.clearBorderLabelState();

      this.requester.recalculate(
        this.editState.backendObjects,
        data => {
          this.editState.update(data);
          this.renderer.updatePlanareas();
          this.activePlanarea.editArea();
          this.renderer.redraw([VisualOptions.AnimateBoards]);
          this.ui.planareaActivated(this.activePlanarea, this.editState.areas.length === 1);
          this.ui.updateStats(this.editState, this.activePlanarea);
        },
        data => {
          this._recalculationFailedHandling(data);
        }
      );
    } else {
      this.activePlanarea.editArea();
      this.renderer.redraw();
    }
  }

  boardsLayoutChanged(index) {
    this.activePlanarea.areaState.currentPlanIndex = index;
    this.activePlanarea.editArea();
    this.ui.planareaActivated(this.activePlanarea, this.editState.areas.length === 1);
    this.ui.updateStats(this.editState, this.activePlanarea);
    this.renderer.redraw();
  }

  boardsLayoutChangedStep(currentPlanIndex, step = 1) {
    const sortedPlans = this.activePlanarea.areaState.indexedBoardLayoutsSorted;
    const index = sortedPlans.findIndex(plan => plan.index === currentPlanIndex);
    let nextLayoutIndex;
    if (sortedPlans[index + step]) {
      nextLayoutIndex = sortedPlans[index + step].index;
    } else if (step > 0) {
      nextLayoutIndex = sortedPlans[0].index;
    } else {
      nextLayoutIndex = sortedPlans[sortedPlans.length - 1].index;
    }
    this.boardsLayoutChanged(nextLayoutIndex);
  }

  selectedBoardLengthsChanged(lengths) {
    const lengthNumbers = lengths.map(length => {
      return parseInt(length);
    });
    const selectedLengths = this.additionalOptions.availableBoards.filter(item => {
      return lengthNumbers.includes(item.length);
    });
    this.activePlanarea.areaState.selectedBoardLengths = selectedLengths;

    this.requester.recalculate(
      this.editState.backendObjects,
      data => {
        this.editState.update(data);
        this.activePlanarea.editArea();
        this.renderer.redraw([VisualOptions.AnimateBoards]);
        this.ui.planareaActivated(this.activePlanarea, this.editState.areas.length === 1);
        this.ui.updateStats(this.editState, this.activePlanarea);
      },
      data => {
        this._recalculationFailedHandling(data);
      }
    );
  }

  splitPerpendicularRequested() {
    this.activePlanarea.editSplitPerpendicular();
    this.renderer.redraw();
  }

  splitParallelRequested() {
    this.activePlanarea.editSplitParallel();
    this.renderer.redraw();
  }

  splitCanceled() {
    this.activePlanarea.editArea();
    this.renderer.redraw();
  }

  planareaSplitted(segment, stateId) {
    try {
      const newAreas = this.editState.split(stateId, segment);
      const idToActivate = newAreas[0].id;

      this.activePlanarea = null;
      this.requester.recalculate(
        this.editState.backendObjects,
        (data => {
          this.editState.update(data);
          this.renderer.refresh();
          this.renderer.activatePlanareaById(idToActivate);
          this.ui.updateStats(this.editState, this.activePlanarea);
        }).bind(this),
        (data => {
          this._recalculationFailedHandling(data);
        }).bind(this)
      );
    } catch (e) {
      console.log(e);
      alert(this.translator.translate('cannotSplit'));
    }
  }

  installationTypeChanged(installationType) {
    if (this.activePlanarea.getInstallationType() === installationType) {
      return;
    }

    this.activePlanarea.areaState.updateRawAreaData('deck_type', installationType.description);
    this.activePlanarea.areaState.updateRawAreaData('current_plan_index', 0);

    this.requester.recalculate(
      this.editState.backendObjects,
      data => {
        this.editState.update(data);
        this.activePlanarea.editArea();
        this.renderer.redraw([VisualOptions.AnimateBoards]);
        this.ui.planareaActivated(this.activePlanarea, this.editState.areas.length === 1);
        this.ui.updateStats(this.editState, this.activePlanarea);
      },
      data => {
        this._recalculationFailedHandling(data);
      }
    );
  }

  breakerBoardsPlaced(numberOfPlanks) {
    if (numberOfPlanks === this.activeBorder.getBreakerBoardPlankCount()) {
      this._deactivateBorder();
      this.ui.planareaActivated(this.activePlanarea, this.editState.areas.length === 1);
      this.activePlanarea.editArea();
      this.renderer.redraw();
      return;
    }

    try {
      this.editState.placeBreakerBoards(this.activePlanarea.id, this.activeBorder.index, numberOfPlanks);
      const idToActivate = this.activePlanarea.id;

      this.activePlanarea = null;
      this.requester.recalculate(
        this.editState.backendObjects,
        (data => {
          this.editState.update(data);
          this.renderer.refresh();
          this.renderer.activatePlanareaById(idToActivate);
          this.ui.planareaActivated(this.activePlanarea, this.editState.areas.length === 1);
          this.ui.updateStats(this.editState, this.activePlanarea);
        }).bind(this),
        (data => {
          this._recalculationFailedHandling(data);
        }).bind(this)
      );
    } catch (e) {
      console.log(e);
      window.alert('Cannot place breaker boards here');
    }
  }

  angleChanged(radians) {
    if (this.activePlanarea.areaState.rawArea.data.angle_rad !== radians) {
      this.activePlanarea.areaState.updateRawAreaData('angle_rad', radians);
      this.activePlanarea.areaState.clearBorderLabelState();

      this.requester.recalculate(
        this.editState.backendObjects,
        data => {
          this.editState.update(data);
          this.renderer.updatePlanareas();
          this.activePlanarea.editArea();
          this.renderer.redraw([VisualOptions.AnimateBoards]);
          this.ui.updateStats(this.editState, this.activePlanarea);
          this.ui.planareaActivated(this.activePlanarea, this.editState.areas.length === 1);
        },
        data => {
          this._recalculationFailedHandling(data);
        }
      );
    }
  }

  joistSpacingPerpendicularChanged(joistSpacing) {
    if (this.activePlanarea.areaState.currentJoistSpacingPerpendicular !== joistSpacing) {
      this.activePlanarea.areaState.currentJoistSpacingPerpendicular = joistSpacing;
      this.requester.recalculate(
        this.editState.backendObjects,
        data => {
          this.editState.update(data);
          this.renderer.updatePlanareas();
          this.activePlanarea.editArea();
          this.renderer.redraw([VisualOptions.AnimateBoards]);
          this.ui.updateStats(this.editState, this.activePlanarea);
          this.ui.planareaActivated(this.activePlanarea, this.editState.areas.length === 1);
        },
        data => {
          this._recalculationFailedHandling(data);
        }
      );
    }
  }

  joistSpacingParallelChanged(joistSpacing) {
    if (this.activePlanarea.areaState.currentJoistSpacingParallel !== joistSpacing) {
      this.activePlanarea.areaState.currentJoistSpacingParallel = joistSpacing;
      this.requester.recalculate(
        this.editState.backendObjects,
        data => {
          this.editState.update(data);
          this.renderer.updatePlanareas();
          this.activePlanarea.editArea();
          this.renderer.redraw([VisualOptions.AnimateBoards]);
          this.ui.updateStats(this.editState, this.activePlanarea);
          this.ui.planareaActivated(this.activePlanarea, this.editState.areas.length === 1);
        },
        data => {
          this._recalculationFailedHandling(data);
        }
      );
    }
  }

  borderDeleted() {
    const newArea = this.editState.deleteBorder(this.activePlanarea.id, this.activeBorder.index);
    const idToActivate = newArea.id;

    this.activePlanarea = null;
    this.requester.recalculate(
      this.editState.backendObjects,
      (data => {
        this.editState.update(data);
        this.renderer.refresh();
        this.renderer.activatePlanareaById(idToActivate);
        this.ui.planareaActivated(this.activePlanarea, this.editState.areas.length === 1);
        this.ui.updateStats(this.editState, this.activePlanarea);
      }).bind(this),
      (data => {
        this._recalculationFailedHandling(data);
      }).bind(this)
    );
  }

  tileShiftChanged(tileShift) {
    const currentTileShift = this.activePlanarea.areaState.currentTileShift;
    if (tileShift === currentTileShift) {
      return;
    }

    this.activePlanarea.areaState.currentTileShift = tileShift;

    this.requester.recalculate(
      this.editState.backendObjects,
      data => {
        this.editState.update(data);
        this.renderer.updatePlanareas();
        this.activePlanarea.editArea();
        this.renderer.redraw([VisualOptions.AnimateBoards]);
        this.ui.planareaActivated(this.activePlanarea, this.editState.areas.length === 1);
        this.ui.updateStats(this.editState, this.activePlanarea);
      },
      data => {
        this._recalculationFailedHandling(data);
      }
    );
  }

  trimLabelChanged() {
    this.renderer.redraw();
  }

  _recalculationFailedHandling(error) {
    console.error(error);
    if (error.code === '') {
      alert(this.translator.translate('serverUnreachableError'));
    } else {
      alert(this.translator.translate('recalcError'));
    }
    window.location.reload();
  }
}
